import { useMemo } from '@frontend/react';
import { Button, Text, YStack } from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';

type StickyNote = {
  color: string;
  label: string;
};

type StickyNotesHeroProps = {
  ctaLabel: string;
  heading: string;
  headingAccent?: string;
  notes: StickyNote[];
  onClick: () => void;
  subheading?: string;
};

/**
 * Pre-defined positions for sticky notes distributed around the perimeter.
 * Each position places a note around the edges so the center remains clear
 * for the headline overlay. Rotations give a natural, hand-placed feel.
 */
const NOTE_POSITIONS: ReadonlyArray<{ left: string; rotate: string; top: string }> = [
  // Top-left region
  { left: '2%', rotate: '-6deg', top: '2%' },
  { left: '1%', rotate: '4deg', top: '28%' },
  { left: '0%', rotate: '-3deg', top: '52%' },
  { left: '3%', rotate: '5deg', top: '72%' },
  // Top-center
  { left: '35%', rotate: '-2deg', top: '0%' },
  { left: '55%', rotate: '3deg', top: '1%' },
  // Right region
  { left: '82%', rotate: '5deg', top: '3%' },
  { left: '80%', rotate: '-4deg', top: '26%' },
  { left: '83%', rotate: '3deg', top: '48%' },
  { left: '81%', rotate: '-5deg', top: '68%' },
  // Bottom region
  { left: '5%', rotate: '2deg', top: '90%' },
  { left: '30%', rotate: '-4deg', top: '92%' },
  { left: '60%', rotate: '6deg', top: '88%' },
  { left: '80%', rotate: '-3deg', top: '90%' },
  // Extra positions for overflow notes
  { left: '15%', rotate: '-5deg', top: '14%' },
  { left: '70%', rotate: '4deg', top: '12%' },
  { left: '12%', rotate: '3deg', top: '84%' },
  { left: '72%', rotate: '-6deg', top: '82%' },
];

const FALLBACK_POSITION = { left: '2%', rotate: '0deg', top: '2%' } as const;

const StickyNoteCard = ({
  note,
  position,
}: {
  note: StickyNote;
  position: { left: string; rotate: string; top: string };
}) => (
  <div
    className={safeCss('absolute shadow-one rounded-sm select-none')}
    style={{
      backgroundColor: note.color,
      left: position.left,
      minHeight: 80,
      padding: 12,
      top: position.top,
      transform: `rotate(${position.rotate})`,
      width: 140,
    }}
  >
    <span className="font-caveat text-p-sm text-neutral-900 break-words" style={{ lineHeight: 1.3 }}>
      {note.label}
    </span>
  </div>
);

export const StickyNotesHero = ({
  ctaLabel,
  heading,
  headingAccent,
  notes,
  onClick,
  subheading,
}: StickyNotesHeroProps) => {
  const positionedNotes = useMemo(
    () =>
      notes.map((note, index) => ({
        note,
        position: NOTE_POSITIONS[index % NOTE_POSITIONS.length] ?? FALLBACK_POSITION,
      })),
    [notes],
  );

  return (
    <div className={safeCss('relative w-full overflow-hidden')} style={{ minHeight: 300 }}>
      {/* Scattered sticky notes layer */}
      {positionedNotes.map(({ note, position }) => (
        <StickyNoteCard key={note.label} note={note} position={position} />
      ))}

      {/* Centered content overlay */}
      <YStack
        alignItems="center"
        className={safeCss('absolute inset-none justify-center pointer-events-none z-10')}
        px="xl"
        py="xxl"
      >
        <YStack alignItems="center" className="pointer-events-auto" gapY="lg" style={{ maxWidth: 720 }}>
          {/* Headline */}
          <YStack alignItems="center" gapY="xs">
            <span className="font-serif text-center tracking-tight" style={{ fontSize: 42, lineHeight: 1.15 }}>
              {heading}
            </span>
            {headingAccent ? (
              <span className="font-serif italic text-center tracking-tight" style={{ fontSize: 42, lineHeight: 1.15 }}>
                {headingAccent}
              </span>
            ) : null}
          </YStack>

          {/* Subheading */}
          {subheading ? (
            <Text className="text-center" color="tertiary" style={{ maxWidth: 560 }} type="p-lg">
              {subheading}
            </Text>
          ) : null}

          {/* CTA Button */}
          <Button
            className={safeCss('mt-sm')}
            onClick={onClick}
            rightIconName="IconArrowRight"
            size="lg"
            variant="primary"
          >
            {ctaLabel}
          </Button>
        </YStack>
      </YStack>
    </div>
  );
};
